import React, { useEffect, useState } from 'react';
import { api } from '../api';

const PROCESS_STEPS = [
  { id: 'recepcion',        step: 1, icon: '📥', label: 'Recepción MP',     desc: 'Registra la llegada de materia prima. Se genera un lote con código único.' },
  { id: 'lotes',            step: 2, icon: '🏪', label: 'Lotes en bodega',  desc: 'El lote queda almacenado. Puedes ver estado, vencimiento y etiqueta.' },
  { id: 'stock',            step: 3, icon: '📊', label: 'Stock disponible', desc: 'El inventario se actualiza automáticamente según ingresos y despachos.' },
  { id: 'pedidos',          step: 4, icon: '🛒', label: 'Pedido a bodega',  desc: 'Cocina solicita insumos eligiendo el lote y cantidad necesaria.' },
  { id: 'recepcion-cocina', step: 5, icon: '✅', label: 'Recepción cocina', desc: 'El chef confirma la recepción. El lote queda trazado hasta cocina.' },
];

function formatFecha() {
  const ahora = new Date();
  const dias = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
  const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
  return `${dias[ahora.getDay()]} ${ahora.getDate()} de ${meses[ahora.getMonth()]}, ${ahora.getFullYear()}`;
}

export default function Dashboard({ onNav }) {
  const [recepciones, setRecepciones] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.getRecepciones(),
      api.getPedidos(),
      api.getEventos(),
    ]).then(([r, p, e]) => {
      setRecepciones(r);
      setPedidos(p);
      setEventos(e);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const hoy = new Date().toISOString().slice(0, 10);
  const lotesActivos = recepciones.filter(r => r.condicion !== 'rej').length;
  const pedidosPendientes = pedidos.filter(p => p.estado !== 'Recepcionado').length;
  const recepcionesHoy = recepciones.filter(r => (r.fecha || '').startsWith(hoy)).length;

  // Alertas dinámicas
  const alertas = recepciones.filter(r => {
    if (!r.vence || r.condicion === 'rej') return false;
    const dias = (new Date(r.vence) - new Date()) / 86400000;
    return dias >= 0 && dias < 7;
  });

  const timelineColors = {
    'Recepción': 'green', 'Despacho cocina': 'blue', 'Pedido cocina': 'blue',
    'Sync SAP': 'primary', 'Alerta vencimiento': 'amber', 'Recepción cocina': 'blue',
  };

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Panel general</div>
          <div className="topbar-meta">{formatFecha()} · Turno mañana</div>
        </div>
        <div className="topbar-actions">
          <div className="sap-pill"><span className="sap-dot" /> SAP S/4HANA sincronizado</div>
        </div>
      </div>

      <div className="page-content">

        {/* ── Guía de proceso ── */}
        <div className="card" style={{ marginBottom: 22 }}>
          <div className="card-title"><div className="ct-icon">⟶</div> ¿Cómo funciona TraceOps?</div>
          <p style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
            TraceOps registra el ciclo completo de la materia prima — desde que entra del proveedor hasta que llega a cocina.
            Cada lote se trazabiliza por código único en 5 pasos:
          </p>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, overflowX: 'auto', paddingBottom: 4 }}>
            {PROCESS_STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <div
                  onClick={() => onNav(s.id)}
                  style={{
                    flex: '1 0 160px', minWidth: 150, cursor: 'pointer',
                    border: '1.5px solid var(--border)',
                    borderRadius: 11, padding: '14px 14px 12px',
                    background: 'var(--surface)',
                    transition: 'all 0.15s',
                    position: 'relative',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--primary-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface)'; }}
                >
                  {/* Número de paso */}
                  <div style={{
                    position: 'absolute', top: 10, right: 10,
                    width: 20, height: 20, borderRadius: 6,
                    background: 'var(--primary)', color: '#fff',
                    fontSize: 10, fontWeight: 800, fontFamily: 'var(--font-display)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{s.step}</div>

                  <div style={{ fontSize: 22, marginBottom: 8 }}>{s.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 5 }}>{s.label}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text2)', lineHeight: 1.5 }}>{s.desc}</div>
                  <div style={{ marginTop: 10, fontSize: 11, color: 'var(--primary)', fontWeight: 700 }}>→ Ir al módulo</div>
                </div>

                {i < PROCESS_STEPS.length - 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px', flexShrink: 0 }}>
                    <svg width="24" height="16" viewBox="0 0 24 16">
                      <line x1="2" y1="8" x2="18" y2="8" stroke="var(--primary-mid)" strokeWidth="2"/>
                      <polyline points="13,3 20,8 13,13" fill="none" stroke="var(--primary-mid)" strokeWidth="2" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── Métricas ── */}
        <div className="stats-grid">
          <div className="stat-card stat-primary" style={{ cursor: 'pointer' }} onClick={() => onNav('lotes')}>
            <div className="stat-label">Lotes activos</div>
            <div className="stat-value">{loading ? '—' : lotesActivos}</div>
            <div className="stat-sub">En bodega · Ver lotes →</div>
          </div>
          <div className="stat-card stat-amber" style={{ cursor: 'pointer' }} onClick={() => onNav('pedidos')}>
            <div className="stat-label">Pedidos en curso</div>
            <div className="stat-value">{loading ? '—' : pedidosPendientes}</div>
            <div className="stat-sub">Sin confirmar · Ver pedidos →</div>
          </div>
          <div className="stat-card stat-blue" style={{ cursor: 'pointer' }} onClick={() => onNav('recepcion')}>
            <div className="stat-label">Recepciones hoy</div>
            <div className="stat-value">{loading ? '—' : recepcionesHoy}</div>
            <div className="stat-sub">Registradas hoy · Registrar →</div>
          </div>
          <div className="stat-card stat-green" style={{ cursor: 'pointer' }} onClick={() => onNav('pipeline')}>
            <div className="stat-label">Alertas de vencimiento</div>
            <div className="stat-value">{loading ? '—' : alertas.length}</div>
            <div className="stat-sub">{alertas.length > 0 ? 'Lotes por vencer · Ver →' : 'Sin alertas activas'}</div>
          </div>
        </div>

        {/* ── Alertas dinámicas ── */}
        {alertas.map(r => {
          const dias = Math.ceil((new Date(r.vence) - new Date()) / 86400000);
          return (
            <div key={r.id} className="alert alert-amber">
              <span style={{ fontSize: 16 }}>⚠</span>
              <div>
                <strong>Alerta de vencimiento:</strong> Lote <strong>{r.lote}</strong> ({r.producto}) vence
                {dias === 0 ? ' hoy' : dias === 1 ? ' mañana' : ` en ${dias} días`} ({r.vence}).
                Se recomienda uso prioritario.
              </div>
            </div>
          );
        })}

        {/* ── Últimas recepciones + Flujo reciente ── */}
        <div className="grid-2">
          <div className="card">
            <div className="card-title"><div className="ct-icon">📋</div> Últimas recepciones</div>
            {loading
              ? <div style={{ textAlign: 'center', padding: 24, color: 'var(--text3)', fontSize: 13 }}>Cargando...</div>
              : recepciones.length === 0
                ? (
                  <div className="empty-state">
                    <div className="es-icon">📥</div>
                    <div className="es-text">Sin recepciones registradas</div>
                    <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => onNav('recepcion')}>
                      + Registrar primera recepción
                    </button>
                  </div>
                )
                : (
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>Producto</th><th>Lote</th><th>Qty</th><th>Estado</th></tr></thead>
                      <tbody>
                        {recepciones.slice(0, 5).map(r => (
                          <tr key={r.id}>
                            <td><strong>{r.producto}</strong></td>
                            <td><span className="lot-tag">{r.lote}</span></td>
                            <td>{r.qty} {r.unidad}</td>
                            <td>
                              {r.condicion === 'ok'
                                ? <span className="badge badge-green">✓ Conforme</span>
                                : r.condicion === 'obs'
                                ? <span className="badge badge-amber">⚠ Con obs.</span>
                                : <span className="badge badge-red">✗ Rechazado</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
            }
            {!loading && recepciones.length > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => onNav('recepcion')}>
                Ver todas las recepciones →
              </button>
            )}
          </div>

          <div className="card">
            <div className="card-title"><div className="ct-icon">📍</div> Actividad reciente</div>
            {loading
              ? <div style={{ textAlign: 'center', padding: 24, color: 'var(--text3)', fontSize: 13 }}>Cargando...</div>
              : eventos.length === 0
                ? <div className="empty-state"><div className="es-icon">📍</div><div className="es-text">Sin actividad registrada</div></div>
                : (
                  <div className="timeline">
                    {eventos.slice(0, 6).map(e => (
                      <div key={e.id || e.ts} className="tl-item">
                        <div className={`tl-dot ${timelineColors[e.evento] || 'green'}`} />
                        <div className="tl-time">{e.ts}</div>
                        <div className="tl-desc">{e.evento}: <strong>{e.producto}</strong></div>
                        <div className="tl-meta">{e.detalle} · {e.responsable}</div>
                      </div>
                    ))}
                  </div>
                )
            }
            {!loading && eventos.length > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => onNav('historial')}>
                Ver historial completo →
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
